import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import {
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Box, Typography, SelectChangeEvent, CircularProgress, Card, CardContent, Chip
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SearchIcon from '@mui/icons-material/Search';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import api from '../api';
import { useLanguage } from '../i18n/LanguageContext';
import BarcodeMealScanner, { ScannedItem } from './BarcodeMealScanner';
import FoodSearchMealAdder from './FoodSearchMealAdder';

interface MealLoggerProps {
  onMealLogged: () => void;
  yesterdayMeals: Array<{
    description: string;
    estimated_calories: number | null;
    meal_type: string;
  }>;
  active: boolean;
}

interface FoodRecognitionItem {
  food_name: string;
  calories: number;
  serving_qty: number;
  serving_unit: string;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  serving_size_g?: number;
}

const MealLogger: React.FC<MealLoggerProps> = ({ onMealLogged, yesterdayMeals, active }) => {
  const [description, setDescription] = useState('');
  const [mealDate, setMealDate] = useState(new Date().toISOString().slice(0, 10));
  const [mealType, setMealType] = useState('');
  const [calories, setCalories] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [macros, setMacros] = useState<{ protein: number; carbs: number; fat: number } | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const lastAnalyzedRef = useRef('');
  const analyzeVersionRef = useRef(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    if (!active) {
      setShowScanner(false);
      setShowSearch(false);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setIsRecording(false);
    }
  }, [active]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
      }
    };
  }, []);

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
  };

  const startRecording = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        if (blob.size === 0) return;
        setIsTranscribing(true);
        try {
          const ext = mimeType === 'audio/webm' ? 'webm' : 'mp4';
          const formData = new FormData();
          formData.append('file', blob, `recording.${ext}`);
          const response = await api.post('/meals/transcribe-audio', formData);
          if (response.data.text) {
            setDescription(response.data.text);
          }
        } catch {
          setError(t.voiceTranscribeFailed);
        } finally {
          setIsTranscribing(false);
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      recordingTimerRef.current = setTimeout(() => {
        stopRecording();
      }, 30000);
    } catch {
      setError(t.micPermissionDenied);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleScanDone = (items: ScannedItem[]) => {
    setShowScanner(false);
    setShowSearch(false);
    const desc = items.map(i => i.product.name).join(', ');
    const cal = items.reduce((s, i) => s + i.calories, 0);
    lastAnalyzedRef.current = desc;
    setDescription(desc);
    setCalories(cal.toString());
    setMacros({
      protein: Math.round(items.reduce((s, i) => s + i.protein_g, 0) * 10) / 10,
      carbs:   Math.round(items.reduce((s, i) => s + i.carbs_g,   0) * 10) / 10,
      fat:     Math.round(items.reduce((s, i) => s + i.fat_g,     0) * 10) / 10,
    });
  };

  const analyzeText = async () => {
    if (!description) return;
    if (description === lastAnalyzedRef.current) return;
    const version = ++analyzeVersionRef.current;
    const textToAnalyze = description;
    setIsAnalyzing(true);
    setError(null);
    try {
      const response = await api.post('/meals/analyze-text', { text: textToAnalyze });
      if (version !== analyzeVersionRef.current) return;
      const items: FoodRecognitionItem[] = response.data;
      if (items.length > 0) {
        const totalCalories = items.reduce((sum, item) => sum + item.calories, 0);
        const totalProtein = items.reduce((sum, item) => sum + (item.protein_g || 0), 0);
        const totalCarbs = items.reduce((sum, item) => sum + (item.carbs_g || 0), 0);
        const totalFat = items.reduce((sum, item) => sum + (item.fat_g || 0), 0);

        lastAnalyzedRef.current = textToAnalyze;
        setCalories(Math.round(totalCalories).toString());
        setMacros({
          protein: Math.round(totalProtein * 10) / 10,
          carbs: Math.round(totalCarbs * 10) / 10,
          fat: Math.round(totalFat * 10) / 10,
        });
      } else {
        lastAnalyzedRef.current = textToAnalyze;
        setError(t.analyzeNoResults);
      }
    } catch {
      if (version !== analyzeVersionRef.current) return;
      setError(t.analyzeTextFailed);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const yesterdaySuggestion = yesterdayMeals.find(m => m.meal_type === mealType) ?? null;

  const handleRepeatYesterday = () => {
    if (!yesterdaySuggestion) return;
    lastAnalyzedRef.current = yesterdaySuggestion.description;
    setDescription(yesterdaySuggestion.description);
    setCalories(yesterdaySuggestion.estimated_calories?.toString() ?? '');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    analyzeVersionRef.current++;
    setIsAnalyzing(false);
    setIsSubmitting(true);
    setError(null);

    try {
      const mealData = {
        id: crypto.randomUUID(),
        user_email: '_',  // overridden server-side from auth token
        timestamp: mealDate === new Date().toISOString().slice(0, 10)
          ? new Date().toISOString()
          : `${mealDate}T12:00:00Z`,
        description,
        estimated_calories: parseFloat(calories) || 0,
        meal_type: mealType,
      };

      await api.post('/meals', mealData);

      lastAnalyzedRef.current = '';
      setDescription('');
      setMealType('');
      setCalories('');
      setError(null);
      setMacros(null);
      onMealLogged();
    } catch (e) {
      console.error('MealLogger: failed to log meal:', e);
      setError(t.logMealFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 4 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t.logYourMeal}
        </Typography>
        {showScanner ? (
          <BarcodeMealScanner onDone={handleScanDone} onCancel={() => setShowScanner(false)} />
        ) : showSearch ? (
          <FoodSearchMealAdder onDone={handleScanDone} onCancel={() => setShowSearch(false)} />
        ) : (
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t.mealType}</InputLabel>
              <Select
                value={mealType}
                label={t.mealType}
                onChange={(e: SelectChangeEvent) => setMealType(e.target.value)}
                required
              >
                <MenuItem value="breakfast">{t.breakfast}</MenuItem>
                <MenuItem value="lunch">{t.lunch}</MenuItem>
                <MenuItem value="dinner">{t.dinner}</MenuItem>
                <MenuItem value="snack">{t.snack}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t.mealDate}
              type="date"
              value={mealDate}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setMealDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().slice(0, 10) }}
            />
          </Box>

          {yesterdaySuggestion && (
            <Chip
              label={`${t.repeatYesterday} — ${yesterdaySuggestion.description} (${Math.round(yesterdaySuggestion.estimated_calories ?? 0)} kcal)`}
              onClick={handleRepeatYesterday}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ mb: 1.5, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
            />
          )}

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t.describeFood}
              placeholder='e.g. "two eggs and toast"'
              value={description}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
              required
              multiline
              rows={2}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t.estimatedCalories}
              type="number"
              value={calories}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setCalories(e.target.value)}
            />
          </Box>

          {macros && (
            <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={`${t.protein}: ${macros.protein}g`} size="small" variant="outlined" />
              <Chip label={`${t.carbs}: ${macros.carbs}g`} size="small" variant="outlined" />
              <Chip label={`${t.fat}: ${macros.fat}g`} size="small" variant="outlined" />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={isAnalyzing ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
              onClick={analyzeText}
              disabled={!description || isAnalyzing || isRecording || isTranscribing || description === lastAnalyzedRef.current}
            >
              {t.analyzeFood}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                isTranscribing ? <CircularProgress size={16} /> :
                isRecording ? <StopIcon /> : <MicIcon />
              }
              onClick={toggleRecording}
              disabled={isTranscribing || isAnalyzing}
              color={isRecording ? 'error' : 'primary'}
              sx={isRecording ? {
                borderColor: 'error.main',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { boxShadow: '0 0 0 0 rgba(244,67,54,0.4)' },
                  '50%': { boxShadow: '0 0 0 6px rgba(244,67,54,0)' },
                },
              } : undefined}
            >
              {isTranscribing ? t.voiceTranscribing : isRecording ? t.voiceRecording : t.voiceRecord}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CameraAltIcon />}
              onClick={() => { analyzeVersionRef.current++; setIsAnalyzing(false); setShowScanner(true); }}
              disabled={isRecording || isTranscribing}
            >
              {t.scanBarcode}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SearchIcon />}
              onClick={() => { analyzeVersionRef.current++; setIsAnalyzing(false); setShowSearch(true); }}
              disabled={isRecording || isTranscribing}
            >
              {t.searchFood}
            </Button>
          </Box>

          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isSubmitting}
          >
            {isSubmitting ? t.logging : t.logMeal}
          </Button>
        </form>
        )}
      </CardContent>
    </Card>
  );
};

export default MealLogger;
